import { useContext, useMemo } from 'react'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Pill,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TypingLoader,
  cn,
} from '@optechai/design-system'
import { format } from 'date-fns'
import { Ticket } from 'lucide-react'

import type { TicketMessageAttachment } from '@server/models/ticketMessage/ticketMessageAttachment.dto'
import { useFeatureFlag } from '@web/services/feature-flag'
import { markdownToReact } from '@web/services/markdown'

import { MediaCard } from '../media-card/media-card'
import {
  WorkflowNodeItem,
  WorkflowNodePill,
} from '../primitive/workflow-node-type'

import { CustomisationContext, InvertAlignmentContext } from './context'
import type { BasicNodeDetails } from './messages'
import { ProgressiveLoadingMessage } from './progressive-loader'

type MessageAlignment = 'left' | 'right'

type MessageItemProps = {
  author: MessageAuthor
  attachments?: TicketMessageAttachment[]
  content?: string
  isLoading?: boolean
  estimatedLoadingDurationMs?: number
  isDraft?: boolean
  isBot?: boolean
  isInternal?: boolean
  isPreviousBot?: boolean
  isUnknown?: boolean
  createdAt?: string
  remoteCreatedAt?: string
  originNodes?: BasicNodeDetails[]
  appearance?: 'primary' | 'secondary' | 'neutral'
}

export type MessageAuthor = {
  /**
   * Image url src for the author avatar
   */
  src?: string
  /**
   * Initials for the author avatar
   */
  initials: string
  /**
   * Alignment of the author avatar - used to determine which side of the message item the avatar should be on
   */
  align: MessageAlignment
  /**
   * Name of the author
   */
  name: string
}

const NodeAttributionPill = ({ node }: { node: BasicNodeDetails }) => {
  if (node.type === 'Return') {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <WorkflowNodePill type={node.type} />
      </TooltipTrigger>
      <TooltipContent>
        <WorkflowNodeItem label={node.displayName} type={node.type} />
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * __MessageItem__
 *
 * A single message item in the conversation.
 */
export const MessageItem = ({
  author,
  content,
  isDraft,
  isInternal,
  isUnknown,
  isLoading,
  isPreviousBot = false,
  originNodes,
  appearance = 'neutral',
  attachments = [],
  // Changed this to 25s based on our analysis that p50 response time is about 22s as of late Mar 2025
  estimatedLoadingDurationMs = 25_000, // 25 seconds
  remoteCreatedAt,
  createdAt,
}: MessageItemProps) => {
  const customisation = useContext(CustomisationContext)
  const message = useMemo(() => {
    if (!content) return null
    return markdownToReact(content)
  }, [content])
  const useProgressiveLoading = useFeatureFlag('progressive-loading-messages')
  const isInvertedAlignment = useContext(InvertAlignmentContext)
  const contextualAlignment = isInvertedAlignment
    ? author.align === 'left'
      ? 'right'
      : 'left'
    : author.align

  return (
    <div
      className={cn(
        contextualAlignment === 'left' ? 'mr-auto' : 'ml-auto',
        'gap-y-s flex min-w-64 max-w-md flex-col',
        isLoading && 'min-w-8',
      )}
    >
      {author.name && !isPreviousBot && !isLoading && (
        <div
          className={cn(
            'flex',
            contextualAlignment === 'left'
              ? 'ml-12 justify-start'
              : 'mr-12 justify-end',
          )}
        >
          <Text color="subtle" variant="p-xs">
            {author.name}
          </Text>
        </div>
      )}

      <div
        className={cn(
          contextualAlignment === 'left' ? 'flex-row' : 'flex-row-reverse',
          'gap-m flex items-start',
        )}
      >
        {author.name && (
          <Avatar appearance={appearance}>
            <AvatarImage alt={author.name} src={author.src} />
            <AvatarFallback>{author.initials.toUpperCase()}</AvatarFallback>
          </Avatar>
        )}

        {isLoading ? (
          <div
            className={cn(
              'px-m py-m rounded-lg bg-neutral',
              isInvertedAlignment ? 'mr-auto' : 'ml-auto',
            )}
          >
            {useProgressiveLoading ? (
              <ProgressiveLoadingMessage
                estimatedDurationMs={estimatedLoadingDurationMs}
              />
            ) : (
              <TypingLoader className="h-3 text-primary" />
            )}
          </div>
        ) : (
          <div className="gap-y-xs flex max-w-md flex-col">
            <div
              className={cn(
                'font-text-s gap-y-s p-m flex flex-col overflow-x-hidden break-words rounded-lg',
                appearance === 'primary' && 'bg-primary-subtle',
                appearance === 'secondary' && 'bg-secondary-subtle',
                appearance === 'neutral' && 'bg-neutral',
              )}
            >
              {attachments.length > 0 && (
                <div className="gap-y-s flex flex-col">
                  {/* Intention to add a smarter 'Media' component to deal with non-image assets */}
                  {attachments.map((attachment) => (
                    <MediaCard key={attachment.id} {...attachment} />
                  ))}
                </div>
              )}
              <div className="prose-sm prose-li:list-disc">{message}</div>
              {(!!originNodes?.length ||
                isDraft ||
                isInternal ||
                isUnknown ||
                (customisation?.showVerboseTimestamps && createdAt)) && (
                <div className="gap-x-s flex items-baseline justify-between">
                  {customisation?.showVerboseTimestamps && createdAt && (
                    <Text className="text-nowrap" color="subtle" variant="p-xs">
                      {format(remoteCreatedAt ?? createdAt, 'pp, d MMM yyyy')}
                    </Text>
                  )}
                  {(!!originNodes?.length ||
                    isDraft ||
                    isInternal ||
                    isUnknown) && (
                    <div className="flex flex-row flex-wrap items-center justify-end">
                      {isDraft && (
                        <Pill icon={Ticket} size="xsmall" variant="white">
                          Draft response
                        </Pill>
                      )}
                      {isInternal && (
                        <Pill icon={Ticket} size="xsmall" variant="white">
                          Internal note
                        </Pill>
                      )}
                      {isUnknown && (
                        <Pill icon={Ticket} size="xsmall" variant="white">
                          Unknown
                        </Pill>
                      )}
                      {Array.isArray(originNodes) && originNodes.length > 0 && (
                        <div className="gap-xs flex max-w-full">
                          {originNodes.map((node) => (
                            <NodeAttributionPill key={node.id} node={node} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
